import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type { ResumoPessoalSocietario, Pessoa, BonusPg, NovoBonusPg, StatusBonusPg } from '@pgcustom/shared-types';
import type { Resultado } from '../../core/resultado.js';

export interface FiltroListaBonus {
  pessoaId?: string;
  status?: StatusBonusPg;
}

/**
 * Adapter do domínio `societario` no frontend — self-service ("meus
 * dados", usado pelos dashboards de Diretor/Sócio Investidor/
 * Colaborador/Técnico) + administração do Bônus PG (fase de
 * finalização, Fase 4 — RBAC `financeiro`/`admin`, mesmo recurso
 * `societario` já usado por aportes/participação). O restante do CRUD
 * administrativo (`/societario/pessoas` fora de listar, `/investimentos`,
 * `/participacoes`, `/retiradas`) continua sem adapter de frontend —
 * fora do escopo desta integração.
 */
export class SocietarioApi {
  constructor(private readonly api: ApiClient) {}

  /** `GET /societario/me` — sempre a própria sessão, nunca aceita id. */
  async meuResumo(): Promise<ResumoPessoalSocietario> {
    const resposta = await this.api.get<{ resumo: ResumoPessoalSocietario }>('/societario/me');
    return resposta.resumo;
  }

  /** `GET /societario/pessoas` — usado só para popular o seletor de beneficiário ao registrar um bônus. */
  async listarPessoas(): Promise<Resultado<Pessoa[]>> {
    try {
      const resposta = await this.api.get<{ pessoas: Pessoa[] }>('/societario/pessoas');
      if (resposta.pessoas.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.pessoas };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar pessoas' };
    }
  }

  /** `GET /societario/bonus` — visão administrativa (todas as pessoas), nunca confundir com `meuResumo().bonus` (só a própria). */
  async listarBonus(filtro: FiltroListaBonus = {}): Promise<Resultado<BonusPg[]>> {
    try {
      const resposta = await this.api.get<{ bonus: BonusPg[] }>('/societario/bonus', { ...filtro });
      if (resposta.bonus.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.bonus };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar bônus PG' };
    }
  }

  /** Sempre nasce `proposto` — nunca aprovado automaticamente (regra discricionária, `societario.service.ts`). */
  async registrarBonus(dado: NovoBonusPg): Promise<BonusPg> {
    const resposta = await this.api.post<{ bonus: BonusPg }>('/societario/bonus', dado);
    return resposta.bonus;
  }

  async aprovarBonus(id: string, aprovadorId: string): Promise<BonusPg> {
    const resposta = await this.api.post<{ bonus: BonusPg }>(`/societario/bonus/${id}/aprovar`, { aprovadorId });
    return resposta.bonus;
  }

  async rejeitarBonus(id: string): Promise<BonusPg> {
    const resposta = await this.api.post<{ bonus: BonusPg }>(`/societario/bonus/${id}/rejeitar`);
    return resposta.bonus;
  }

  /** `transacaoId` referencia uma transação REAL já lançada em Financeiro — este adapter nunca cria a transação, só referencia (mesmo contrato do backend, nenhuma segunda contabilidade). */
  async pagarBonus(id: string, transacaoId: string): Promise<BonusPg> {
    const resposta = await this.api.post<{ bonus: BonusPg }>(`/societario/bonus/${id}/pagar`, { transacaoId });
    return resposta.bonus;
  }

  async cancelarBonus(id: string): Promise<BonusPg> {
    const resposta = await this.api.post<{ bonus: BonusPg }>(`/societario/bonus/${id}/cancelar`);
    return resposta.bonus;
  }
}
